import { cloneElement, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Columns, Column, Heading, Stack, breakpoints } from '@etg/wings';
import { useSiteContext, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { connect } from 'react-redux';
import { change, formValueSelector } from 'redux-form';
import { COUNTRY_CODES, OPTIONAL, REQUIRED, NOT_REQUIRED, TRAVEL_DOCUMENT_EXPIRY_DATE, TRAVEL_DOCUMENT_ISSUING_COUNTRY, TRAVEL_DOCUMENT_TYPE, TRAVELER_FORM_SECTION_NAME } from '../../utils/constants';
import { CPF, INTERNATIONAL_PASSPORT_NUMBER_INPUT, PASSPORT } from './utils/constants';
import TravelDocumentIssuingCountry from './TravelDocumentIssuingCountry';
import TravelDocumentNumber from './TravelDocumentNumber';
import TravelDocumentExpiryDate from './TravelDocumentExpiryDate';
import TravelDocumentType from './TravelDocumentType';
import { useDocumentTypes } from './utils/hooks';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const getExpiryDateInput = (labelRef, dateFormat, form, id, requireTravelDocumentExpiryDate, t) => {
  if (!requireTravelDocumentExpiryDate || requireTravelDocumentExpiryDate === NOT_REQUIRED) {
    return null;
  }
  const textKeys = {
    OPTIONAL: t('TD.PassportExpirationDateNoValidation.Label'),
    REQUIRED: t('TravelerForm.TravelDocumentExpiryDate.Label')
  };
  const hintTextKeys = {
    OPTIONAL: null,
    REQUIRED: t('TravelerForm.TravelDocumentExpiryDate.HintLabel', dateFormat.toUpperCase())
  };
  const conditionalProps = {
    isRequired: requireTravelDocumentExpiryDate === REQUIRED,
    label: textKeys[requireTravelDocumentExpiryDate],
    hint: hintTextKeys[requireTravelDocumentExpiryDate],
    validationRuleName: requireTravelDocumentExpiryDate === REQUIRED ? TRAVEL_DOCUMENT_EXPIRY_DATE : null
  };
  return /*#__PURE__*/_jsx(TravelDocumentExpiryDate, {
    dateFormat: dateFormat,
    form: form,
    id: id,
    labelRef: labelRef,
    ...conditionalProps
  });
};
const columnWidth = {
  [breakpoints._0]: '4/4',
  [breakpoints._560]: '2/4'
};
const isTwoColumnLayout = () => window.matchMedia(`(min-width: ${breakpoints._560})`).matches;
const getLabelStyles = height => css`
  & label,
  & [data-label] {
    align-items: flex-end;
    display: flex;
    height: ${height}px;
  }
`;
const getKey = index => `input-${index}`;
const mapStateToProps = (state, _ref) => {
  let {
    form,
    id
  } = _ref;
  return {
    selectedDocumentType: formValueSelector(form)(state, `${TRAVELER_FORM_SECTION_NAME}-${id}.${TRAVEL_DOCUMENT_TYPE}`),
    selectedIssuingCountry: formValueSelector(form)(state, `${TRAVELER_FORM_SECTION_NAME}-${id}.${TRAVEL_DOCUMENT_ISSUING_COUNTRY}`)
  };
};
const mapDispatchToProps = (dispatch, _ref2) => {
  let {
    form,
    id
  } = _ref2;
  return {
    setSelectedDocumentType(selectedOption) {
      dispatch(change(form, `${TRAVELER_FORM_SECTION_NAME}-${id}.${TRAVEL_DOCUMENT_TYPE}`, selectedOption));
    }
  };
};
const TravelDocument = _ref3 => {
  let {
    countries,
    dateFormat = 'yyyy-mm-dd',
    form,
    id,
    selectedDocumentType,
    setSelectedDocumentType,
    selectedIssuingCountry,
    shouldShowTravelDocumentCpfNumber,
    shouldShowTravelDocumentNationality,
    shouldShowTravelDocumentPassportNumber,
    shouldShowTravelDocumentType,
    requireTravelDocumentExpiryDate
  } = _ref3;
  const {
    t
  } = useTranslation();
  const {
    market
  } = useSiteContext();
  const [labelHeights, setLabelHeights] = useState([]);
  const documentTypeLabelRef = useRef(null);
  const documentNumberLabelRef = useRef(null);
  const documentNationalityLabelRef = useRef(null);
  const documentExpiryDateLabelRef = useRef(null);
  const documentTypes = useDocumentTypes(market.code, selectedIssuingCountry === null || selectedIssuingCountry === void 0 ? void 0 : selectedIssuingCountry.code, shouldShowTravelDocumentCpfNumber);
  const selectedDocumentTypeOption = documentTypes.find(option => option.value === selectedDocumentType);
  const firstDocumentTypeOption = documentTypes[0];
  useEffect(() => {
    if (market.code !== COUNTRY_CODES.BR) {
      var _firstDocumentTypeOpt;
      setSelectedDocumentType((_firstDocumentTypeOpt = firstDocumentTypeOption === null || firstDocumentTypeOption === void 0 ? void 0 : firstDocumentTypeOption.value) !== null && _firstDocumentTypeOpt !== void 0 ? _firstDocumentTypeOpt : INTERNATIONAL_PASSPORT_NUMBER_INPUT);
    }
  }, [firstDocumentTypeOption, market.code, setSelectedDocumentType]);
  useEffect(() => {
    const collectedLabelHeights = [documentTypeLabelRef, documentNumberLabelRef, documentNationalityLabelRef, documentExpiryDateLabelRef].map(labelRef => {
      var _labelRef$current;
      return labelRef === null || labelRef === void 0 || (_labelRef$current = labelRef.current) === null || _labelRef$current === void 0 ? void 0 : _labelRef$current.getBoundingClientRect().height;
    }).filter(Boolean);
    setLabelHeights(collectedLabelHeights);
  }, []);
  let shouldShowTravelDocumentNumberInput = false;
  if (market.code === COUNTRY_CODES.BR && (selectedIssuingCountry === null || selectedIssuingCountry === void 0 ? void 0 : selectedIssuingCountry.code) === COUNTRY_CODES.BR && shouldShowTravelDocumentCpfNumber && shouldShowTravelDocumentType) {
    if (documentTypes.length > 1 && selectedDocumentTypeOption) {
      shouldShowTravelDocumentNumberInput = true;
    }
  } else if (shouldShowTravelDocumentPassportNumber || documentTypes.length > 1) {
    shouldShowTravelDocumentNumberInput = true;
  }
  const isDocumentTypePassport = (selectedDocumentTypeOption === null || selectedDocumentTypeOption === void 0 ? void 0 : selectedDocumentTypeOption.type) === PASSPORT;
  let shouldShowExpiryDateInput;
  if (market.code === COUNTRY_CODES.BR && (selectedIssuingCountry === null || selectedIssuingCountry === void 0 ? void 0 : selectedIssuingCountry.code) === COUNTRY_CODES.BR && shouldShowTravelDocumentCpfNumber && shouldShowTravelDocumentType) {
    if ((isDocumentTypePassport || requireTravelDocumentExpiryDate === REQUIRED) && selectedDocumentTypeOption && selectedDocumentTypeOption.value !== CPF) {
      shouldShowExpiryDateInput = true;
    }
  } else if (isDocumentTypePassport || requireTravelDocumentExpiryDate === REQUIRED) {
    shouldShowExpiryDateInput = true;
  }
  const travelDocumentInputs = [shouldShowTravelDocumentNationality && /*#__PURE__*/_jsx(TravelDocumentIssuingCountry, {
    countries: countries,
    form: form,
    id: id,
    labelRef: documentNationalityLabelRef
  }), shouldShowTravelDocumentType && documentTypes.length > 1 && /*#__PURE__*/_jsx(TravelDocumentType, {
    countryCode: market.code,
    form: form,
    id: id,
    labelRef: documentTypeLabelRef,
    options: documentTypes
  }), shouldShowTravelDocumentNumberInput && /*#__PURE__*/_jsx(TravelDocumentNumber, {
    documentCountryCode: selectedIssuingCountry === null || selectedIssuingCountry === void 0 ? void 0 : selectedIssuingCountry.code,
    form: form,
    id: id,
    isDocumentTypePassport: isDocumentTypePassport,
    labelRef: documentNumberLabelRef,
    selectedDocument: selectedDocumentTypeOption
  }), shouldShowExpiryDateInput && getExpiryDateInput(documentExpiryDateLabelRef, dateFormat, form, id, requireTravelDocumentExpiryDate, t)].filter(Boolean);
  const fieldsetStyles = css`
    border: none;
  `;
  return /*#__PURE__*/_jsx(Stack, {
    className: "travelerDetailsSection",
    "data-testid": "travelerDetails-travelDocument-wrapper",
    spacing: 24,
    children: /*#__PURE__*/_jsxs("fieldset", {
      "aria-labelledby": `travel-document-details-${id}`,
      className: fieldsetStyles,
      children: [/*#__PURE__*/_jsx(Heading, {
        id: `travel-document-details-${id}`,
        level: 3,
        children: t('TravelerForm.TravelDocument.Label')
      }), /*#__PURE__*/_jsx(Columns, {
        spacing: 24,
        children: travelDocumentInputs.map((input, index) => {
          const even = index % 2 === 0;
          const currentLabel = labelHeights[index];
          const comparedLabel = labelHeights[even ? index + 1 : index - 1];
          const computedLabelHeight = Math.max(currentLabel, comparedLabel !== null && comparedLabel !== void 0 ? comparedLabel : 0);
          return /*#__PURE__*/_jsx(Column, {
            width: columnWidth,
            children: /*#__PURE__*/cloneElement(input, {
              ...input.props,
              className: cx(input.props.className, isTwoColumnLayout() && getLabelStyles(computedLabelHeight))
            })
          }, getKey(index));
        })
      })]
    })
  });
};
TravelDocument.propTypes = {
  countries: PropTypes.arrayOf(PropTypes.object),
  dateFormat: PropTypes.string,
  form: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requireTravelDocumentExpiryDate: PropTypes.oneOf([OPTIONAL, REQUIRED, NOT_REQUIRED]),
  selectedDocumentType: PropTypes.string,
  selectedIssuingCountry: PropTypes.shape({
    code: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string
  }),
  setSelectedDocumentType: PropTypes.func.isRequired,
  shouldShowTravelDocumentCpfNumber: PropTypes.bool,
  shouldShowTravelDocumentNationality: PropTypes.bool,
  shouldShowTravelDocumentPassportNumber: PropTypes.bool,
  shouldShowTravelDocumentType: PropTypes.bool
};
export default connect(mapStateToProps, mapDispatchToProps)(TravelDocument);
//# sourceMappingURL=TravelDocument.js.map